import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE } from "@/data/site";

/**
 * SEO head manager. Sets <title>, meta description, canonical, OG/Twitter tags
 * and injects JSON-LD structured data. Cleans injected JSON-LD on unmount.
 */
export default function Seo({ title, description, jsonLd = [], image }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title || SITE.name;
    document.title = fullTitle;

    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const canonicalUrl = `${SITE.url}${pathname}`;
    const img = image || `${SITE.url}/favicon.png`;

    if (description) setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description || SITE.description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", img);
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description || SITE.description);
    setMeta("name", "twitter:image", img);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const scripts = [];
    jsonLd.forEach((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      scripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, pathname, JSON.stringify(jsonLd)]);

  return null;
}
